import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material'
import OrderDeliveryModal from '../Modals/OrderDeliveryModal'
import { useTranslation } from 'react-i18next'
import { formatDeliveryTimeRange, getDate } from '../../lib/utils'
import { SelectStatus, Pagination } from '../../components'
import { deliveryOrderStatuses } from '../../lib/statuses'
import { MODES, STATUSES } from '../../lib/constants'
import { gqlOrderDelivery, gqlCommand } from '../../gql'
import { useQuery } from '@apollo/client'
import MaterialsSummary from '../Modals/MaterialsSummary'

function DeliveryOrder({ orderId }) {
  const { t } = useTranslation('order_detail')

  const [deliveryOrders, setDeliveryOrders] = useState([])
  const [orderDelivery, setOrderDelivery] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState(MODES.CREATE)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [first, setFirst] = useState(rowsPerPage)
  const [last, setLast] = useState(null)
  const [after, setAfter] = useState(null)
  const [before, setBefore] = useState(null)
  const [page, setPage] = useState(0)
  const [pageInfo, setPageInfo] = useState(null)
  const [totalCount, setTotalCount] = useState(0)
  const totalPages = Math.ceil(totalCount / rowsPerPage) ?? 0
  const {
    data,
    error: errorDeliveryOrder,
    loading: loadingDeliveryOrder,
    refetch,
  } = useQuery(gqlOrderDelivery.ORDER_DELIVERIES_BY_COMMAND_ID, {
    variables: {
      first,
      after,
      before,
      last,
      commandId: orderId,
    },
  })
  const {
    data: command,
    error: errorCommand,
    loading: loadingCommand,
  } = useQuery(gqlCommand.COMMAND_BY_ID, {
    variables: { id: orderId },
  })
  useEffect(() => {
    if (data) {
      const transformedOrders = data.orderDeliveries.edges.map(({ node }) => ({
        ...node, // Spread all fields from node directly
        clientName: node.command.clientName,
        driverName: node.user ? `${node.user.firstname} ${node.user.lastname}` : '',
        allMaterials: node.orderDeliveryMaterials.nodes.map((materialNode) => ({
          id: materialNode.id,
          materialId: materialNode.materialId,
          commandMaterialId: materialNode.commandMaterialId,
          internalId: materialNode.material.internalId,
          description: materialNode.material.description,
          unit: materialNode.material.measureUnitByBaseMeasureUnitCode.abbr,
          quantity: materialNode.quantity,
          family: materialNode.material.productCategoryByIdProductCategory.family,
        })),
        startingSiteToDisplay: node.startingSiteBdn
          ? {
              address: node.startingSiteBdn.address,
              code: node.startingSiteBdn.code,
            }
          : node.startingSite
          ? {
              address: node.startingSite.address,
              city: node.startingSite.city,
              code: node.startingSite.code,
            }
          : {
              address: node.startingSiteAddress,
              city: node.startingSiteCity,
            },
        deliverySiteToDisplay: node.deliverySiteBdn
          ? {
              address: node.deliverySiteBdn.address,
              code: node.deliverySiteBdn.code,
            }
          : node.deliverySite
          ? {
              address: node.deliverySite.address,
              city: node.deliverySite.city,
              code: node.deliverySite.code,
            }
          : {
              address: node.deliverySiteAddress,
              city: node.deliverySiteCity,
            },
        unloadingSiteToDisplay: node.unloadingSiteBdn
          ? {
              address: node.unloadingSiteBdn.address,
              code: node.unloadingSiteBdn.code,
            }
          : node.unloadingSite
          ? {
              address: node.unloadingSite.address,
              city: node.unloadingSite.city,
              code: node.unloadingSite.code,
            }
          : {
              address: node.unloadingSiteAddress,
              city: node.unloadingSiteCity,
            },
      }))

      setDeliveryOrders(transformedOrders)
      setPageInfo(data.orderDeliveries.pageInfo)
      setTotalCount(data.orderDeliveries.totalCount)
    }
  }, [data])
  const handleChangePage = (newPage, type) => {
    const isNext = type === 'next'
    setAfter(isNext ? pageInfo.endCursor : null)
    setBefore(isNext ? null : pageInfo.startCursor)
    setFirst(isNext ? rowsPerPage : null)
    setLast(isNext ? null : rowsPerPage)
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage)
    setFirst(newRowsPerPage)
    setLast(null)
    setAfter(null)
    setBefore(null)
    setPage(0)
  }

  const loading = loadingDeliveryOrder || loadingCommand
  const error = errorDeliveryOrder || errorCommand
  const handleAddOrderDelivery = () => {
    setOrderDelivery({
      id: '',
      deliveryDate: '',
      deliveryTimeStart: '',
      deliveryTimeEnd: '',
      startingSite: { id: '', code: '', address: '', city: '' },
      deliverySite: { id: '', code: '', address: '', city: '' },
      unloadingSite: { id: '', code: '', address: '', city: '' },
      status: STATUSES.INCOMPLETE,
      commentPublic: '',
      commentPrivate: '',
      commentDriver: '',
      commandId: orderId,
      orderDeliveryNumber: '',
      allMaterials: [],
    })
    setModalMode(MODES.CREATE)
    setIsModalOpen(true)
  }

  const handleEditAndDuplicateClick = (order, mode) => {
    if (mode == MODES.EDIT) {
      setOrderDelivery({
        ...order,
        deliveryDate: order.deliveryDate || '',
        deliveryTimeStart: order.deliveryTimeStart || '',
        deliveryTimeEnd: order.deliveryTimeEnd || '',
      })
    } else if (mode == MODES.DUPLICATE) {
      setOrderDelivery((prevOrderDelivery) => ({
        ...prevOrderDelivery, // On conserve les propriétés existantes de l'objet orderDelivery
        ...order, // On récupère les propriétés de l'objet order
        status: STATUSES.INCOMPLETE,
        commentPrivate: '',
        commentDriver: '',
        user: null,
        userId: null,
      }))
    }
    setModalMode(mode)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setOrderDelivery(null)
  }

  const handleRefreshList = async () => {
    try {
      await refetch()
    } catch (error) {
      console.error('Error refreshing list:', error)
    }
  }

  return (
    <>
      <Card>
        <CardContent>
          {/*button to add new order */}
          <Grid container justifyContent="flex-end" style={{ marginBottom: '3%' }}>
            <Button variant="contained" color="primary" onClick={handleAddOrderDelivery}>
              <span style={{ fontSize: '1.2rem', marginRight: '5px' }}>+</span>
              {t('createDeliveryOrder')}
            </Button>
          </Grid>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '70vh', // Full viewport height
            }}
          >
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
              {/* Materials Summary */}
              {command && <MaterialsSummary command={command} deliveryOrders={deliveryOrders} />}

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                  <CircularProgress
                    size={80}
                    sx={{
                      svg: {
                        width: '100%',
                        height: '100%',
                        viewBox: '0 0 40 40',
                      },
                    }}
                  />
                </Box>
              ) : error ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                  <Typography color="error">{t('errorLoadingData')}</Typography>
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableBody>
                      {totalCount > 0 ? (
                        deliveryOrders.map((deliveryOrder) => {
                          return (
                            <TableRow key={deliveryOrder.id}>
                              <TableCell>
                                <div>{getDate(deliveryOrder.deliveryDate)} {formatDeliveryTimeRange(deliveryOrder.deliveryTimeStart, deliveryOrder.deliveryTimeEnd)}</div>
                                <div>{deliveryOrder.clientName}</div>

                                <div>{deliveryOrder.orderDeliveryNumber}</div>
                                <div style={{ margin: '1%' }}>
                                  <SelectStatus
                                    status={deliveryOrder.status}
                                    statuses={deliveryOrderStatuses()}
                                    isDisabled={true}
                                  />
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  {/* Starting Site */}
                                  {t('startingSite')}
                                  {' : '}
                                  {deliveryOrder.startingSiteToDisplay?.address ||
                                  deliveryOrder.startingSiteToDisplay?.city ||
                                  deliveryOrder.startingSiteToDisplay?.code ? (
                                    <>
                                      {deliveryOrder.startingSiteToDisplay?.address}{' '}
                                      {deliveryOrder.startingSiteToDisplay?.city}{' '}
                                      {deliveryOrder.startingSiteToDisplay?.code || ''}
                                    </>
                                  ) : (
                                    t('undefined')
                                  )}
                                </div>
                                <div>
                                  {/* Delivery Site */}
                                  {t('deliverySite')}
                                  {' : '}
                                  {deliveryOrder.deliverySiteToDisplay?.address ||
                                  deliveryOrder.deliverySiteToDisplay?.city ||
                                  deliveryOrder.deliverySiteToDisplay?.code ? (
                                    <>
                                      {deliveryOrder.deliverySiteToDisplay?.address}{' '}
                                      {deliveryOrder.deliverySiteToDisplay?.city}{' '}
                                      {deliveryOrder.deliverySiteToDisplay?.code || ''}
                                    </>
                                  ) : (
                                    t('undefined')
                                  )}
                                </div>
                                <div>
                                  {/* Unloading Site */}
                                  {t('unloadingSite')}
                                  {' : '}
                                  {deliveryOrder.unloadingSiteToDisplay?.address ||
                                  deliveryOrder.unloadingSiteToDisplay?.city ||
                                  deliveryOrder.unloadingSiteToDisplay?.code ? (
                                    <>
                                      {deliveryOrder.unloadingSiteToDisplay?.address}{' '}
                                      {deliveryOrder.unloadingSiteToDisplay?.city}{' '}
                                      {deliveryOrder.unloadingSiteToDisplay?.code || ''}
                                    </>
                                  ) : (
                                    t('undefined')
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {deliveryOrder.allMaterials?.length > 0 &&
                                  deliveryOrder.allMaterials.map((material, index) => (
                                    <div key={index}>
                                      {material.internalId}, {material.quantity} {material.unit}
                                    </div>
                                  ))}
                              </TableCell>
                              <TableCell>
                                <div>{deliveryOrder.driverName} </div>
                                <div>
                                  {deliveryOrder.scanCmr ? (
                                    <span>
                                      {' '}
                                      <img
                                        src="icons/picto-check.svg"
                                        alt="check Icon"
                                        style={{ width: '24px', height: '24px' }}
                                      />
                                    </span>
                                  ) : (
                                    <img
                                      src="icons/picto-question.svg"
                                      alt="question Icon"
                                      style={{ width: '24px', height: '24px' }}
                                    />
                                  )}
                                  <span>{t('cmr')}</span>
                                </div>
                                <div>
                                  {deliveryOrder.scanProviderDeliveryNote ? (
                                    <span>
                                      {' '}
                                      <img
                                        src="icons/picto-check.svg"
                                        alt="check Icon"
                                        style={{ width: '24px', height: '24px' }}
                                      />
                                    </span>
                                  ) : (
                                    <img
                                      src="icons/picto-question.svg"
                                      alt="question Icon"
                                      style={{ width: '24px', height: '24px' }}
                                    />
                                  )}
                                  <span>{t('providerNote')}</span>
                                </div>
                                <div>
                                  {deliveryOrder.scanSignature ? (
                                    <span>
                                      {' '}
                                      <img
                                        src="icons/picto-check.svg"
                                        alt="check Icon"
                                        style={{ width: '24px', height: '24px' }}
                                      />
                                    </span>
                                  ) : (
                                    <img
                                      src="icons/picto-question.svg"
                                      alt="question Icon"
                                      style={{ width: '24px', height: '24px' }}
                                    />
                                  )}
                                  <span>{t('signature')}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div>
                                  <Button
                                    onClick={() =>
                                      handleEditAndDuplicateClick(deliveryOrder, MODES.EDIT)
                                    }
                                  >
                                    <img
                                      src="icons/picto-edit.svg"
                                      alt="edit Icon"
                                      style={{ width: '24px', height: '24px' }}
                                    />
                                  </Button>
                                </div>
                                <div>
                                  <Button
                                    onClick={() =>
                                      handleEditAndDuplicateClick(deliveryOrder, MODES.DUPLICATE)
                                    }
                                  >
                                    <img
                                      src="icons/picto-duplicate.svg"
                                      alt="edit Icon"
                                      style={{ width: '24px', height: '24px' }}
                                    />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                              {t('noDeliveryOrder')}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
            <Box sx={{ position: 'sticky', bottom: 0, background: 'white', zIndex: 1 }}>
              <Pagination
                page={page}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
      {orderDelivery && (
        <OrderDeliveryModal
          open={isModalOpen}
          onClose={handleModalClose}
          data={orderDelivery}
          mode={modalMode}
          refresh={handleRefreshList}
          commandId={orderId}
        />
      )}
    </>
  )
}
DeliveryOrder.propTypes = {
  orderId: PropTypes.string.isRequired,
}
export default DeliveryOrder